import { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Search, Fingerprint, ImageIcon, Plane, ShieldAlert, Bot, FolderKanban, Settings2, Database,
  Map, Share2, BellRing, Sun, Moon, LogOut, Languages, CornerDownLeft,
} from "lucide-react";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { TYPE_LABELS } from "@/lib/i18n";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";

const NAV = [
  { group: "group_intel", items: [
    { to: "/", key: "dashboard", icon: LayoutDashboard, end: true },
    { to: "/search", key: "search", icon: Search },
    { to: "/identity", key: "identity", icon: Fingerprint },
    { to: "/image", key: "image", icon: ImageIcon },
    { to: "/air", key: "air", icon: Plane },
  ]},
  { group: "group_analysis", items: [
    { to: "/cyber", key: "cyber", icon: ShieldAlert },
    { to: "/ai", key: "ai", icon: Bot },
    { to: "/dossiers", key: "dossiers", icon: FolderKanban },
    { to: "/graph", key: "graph", icon: Share2 },
    { to: "/monitor", key: "monitor", icon: BellRing },
    { to: "/geo", key: "geo", icon: Map },
  ]},
  { group: "group_gov", items: [
    { to: "/sources", key: "sources", icon: Database },
    { to: "/admin", key: "admin", icon: Settings2, roles: ["admin"] },
  ]},
];

function NavItem({ it }) {
  const { t, user } = useApp();
  if (it.roles && !it.roles.includes(user.role)) return null;
  const Icon = it.icon;
  if (it.planned)
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div data-testid={`nav-${it.key}-planned`} className="flex items-center gap-3 px-3 py-2 text-sm text-muted-foreground/50 cursor-not-allowed">
            <Icon size={16} /> <span>{t(it.key)}</span>
            <span className="ml-auto text-[9px] font-mono border rounded px-1">{it.planned}</span>
          </div>
        </TooltipTrigger>
        <TooltipContent side="right">Module planifié — non encore livré (aucun bouton fictif)</TooltipContent>
      </Tooltip>
    );
  return (
    <NavLink to={it.to} end={it.end} data-testid={`nav-${it.key}`}
      className={({ isActive }) => `flex items-center gap-3 px-3 py-2 text-sm rounded-md transition-colors duration-150 ${
        isActive ? "bg-primary/10 text-primary border-l-2 border-primary" : "text-muted-foreground hover:text-foreground hover:bg-accent"}`}>
      <Icon size={16} /> <span>{t(it.key)}</span>
    </NavLink>
  );
}

function Sidebar() {
  const { t } = useApp();
  return (
    <aside data-testid="sidebar-nav" className="w-60 shrink-0 border-r bg-card/60 flex flex-col h-screen sticky top-0">
      <div className="gabon-bar" />
      <div className="flex items-center gap-3 px-4 py-4 border-b">
        <img src="/logo.png" alt="MADENGI" className="w-10 h-10 object-contain" />
        <div>
          <div className="font-display font-extrabold tracking-[0.18em] text-lg gold-text leading-none">MADENGI</div>
          <div className="eyebrow mt-1 !text-[8px]">OSINT · CTI · GEOINT</div>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto px-2 py-4 space-y-5">
        {NAV.map((g) => (
          <div key={g.group}>
            <div className="eyebrow px-3 mb-2">{t(g.group)}</div>
            <div className="space-y-0.5">{g.items.map((it) => <NavItem key={it.key} it={it} />)}</div>
          </div>
        ))}
      </nav>
      <div className="px-4 py-3 border-t text-[10px] font-mono text-muted-foreground">v1.0 · sans abonnement · open source</div>
    </aside>
  );
}

function UniversalSearch() {
  const { t } = useApp();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [det, setDet] = useState(null);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if ((e.ctrlKey || e.metaKey) && e.key === "k") { e.preventDefault(); ref.current?.focus(); } };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);
  useEffect(() => {
    if (q.trim().length < 2) return setDet(null);
    const id = setTimeout(() => api.get("/search/detect", { params: { q } }).then((r) => setDet(r.data)).catch(() => {}), 250);
    return () => clearTimeout(id);
  }, [q]);
  const submit = (e) => {
    e.preventDefault();
    if (q.trim().length >= 2) nav(`/search?q=${encodeURIComponent(q.trim())}`);
  };
  return (
    <form onSubmit={submit} className="flex-1 max-w-3xl relative" data-testid="universal-search-form">
      <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
      <input ref={ref} value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search_placeholder")}
        data-testid="universal-search-input"
        className="w-full h-10 pl-10 pr-44 rounded-md bg-background border font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary/40" />
      <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-2">
        {det && <span data-testid="universal-search-detected-type" className="text-[10px] font-mono px-2 py-0.5 rounded bg-primary/15 text-primary border border-primary/30">{TYPE_LABELS[det.type]}</span>}
        <button type="submit" data-testid="universal-search-submit" className="h-7 px-2 rounded bg-primary text-primary-foreground text-xs flex items-center gap-1 hover:opacity-90">
          <CornerDownLeft size={12} /> {t("run")}
        </button>
      </div>
    </form>
  );
}

function AlertBell() {
  const nav = useNavigate();
  const [d, setD] = useState({ count: 0, latest: [] });
  const [open, setOpen] = useState(false);
  const prev = useRef(null);
  useEffect(() => {
    const load = () => api.get("/monitor/alerts/unread").then((r) => {
      if (prev.current !== null && r.data.count > prev.current) toast.warning(`Nouvelle alerte de veille : ${r.data.latest[0]?.title || ""}`);
      prev.current = r.data.count; setD(r.data);
    }).catch(() => {});
    load();
    const i = setInterval(load, 30000);
    return () => clearInterval(i);
  }, []);
  return (
    <div className="relative">
      <button data-testid="alert-bell-button" onClick={() => setOpen(!open)} className="h-9 w-9 rounded-md border grid place-items-center hover:bg-accent relative">
        <BellRing size={15} />{d.count > 0 && <span data-testid="alert-bell-count" className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-mono grid place-items-center">{d.count > 99 ? "99+" : d.count}</span>}
      </button>
      {open && (
        <div className="absolute right-0 top-11 w-96 panel shadow-2xl p-2 z-[1100]" data-testid="alert-bell-panel">
          <div className="eyebrow px-2 py-1">Alertes non lues ({d.count})</div>
          {d.latest.map((a) => <div key={a.id} className="px-2 py-2 border-t text-xs"><div className="font-mono text-[10px] text-muted-foreground">{a.watch_name} · {a.priority}</div><div className="truncate">{a.title}</div></div>)}
          {!d.latest.length && <div className="px-2 py-3 text-xs text-muted-foreground">Aucune alerte non lue.</div>}
          <button data-testid="alert-bell-open-monitor" onClick={() => { setOpen(false); nav("/monitor"); }} className="w-full mt-1 h-8 rounded bg-primary text-primary-foreground text-xs">Ouvrir la veille</button>
        </div>)}
    </div>
  );
}

function Topbar() {
  const { theme, setTheme, lang, setLang, user, logout, t } = useApp();
  return (
    <header data-testid="app-header" className="h-16 sticky top-0 z-[1000] backdrop-blur-xl bg-background/80 border-b flex items-center gap-4 px-6">
      <UniversalSearch />
      <div className="ml-auto flex items-center gap-2">
        <AlertBell />
        <button data-testid="language-toggle-button" onClick={() => setLang(lang === "fr" ? "en" : "fr")} title={t("language")}
          className="h-9 px-3 rounded-md border text-xs font-mono flex items-center gap-1 hover:bg-accent"><Languages size={14} />{lang.toUpperCase()}</button>
        <button data-testid="theme-toggle-button" onClick={() => setTheme(theme === "dark" ? "light" : "dark")} title={t("theme")}
          className="h-9 w-9 rounded-md border grid place-items-center hover:bg-accent">{theme === "dark" ? <Sun size={15} /> : <Moon size={15} />}</button>
        <div className="text-right px-2 hidden md:block">
          <div className="text-xs font-medium" data-testid="current-user-name">{user.name}</div>
          <div className="text-[10px] font-mono uppercase text-primary" data-testid="current-user-role">{user.role}</div>
        </div>
        <button data-testid="logout-button" onClick={logout} title={t("logout")} className="h-9 w-9 rounded-md border grid place-items-center hover:bg-destructive/20"><LogOut size={15} /></button>
      </div>
    </header>
  );
}

export default function Layout() {
  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar />
      <div className="flex-1 min-w-0 flex flex-col">
        <Topbar />
        <main className="flex-1 p-6 lg:p-8 min-w-0"><Outlet /></main>
      </div>
    </div>
  );
}
